import { Container, Graphics, Sprite } from 'pixi.js';
import { GlowFilter } from '@pixi/filter-glow';
import { rnd, rndI } from './util';
import { SPRITES } from './sprites';

export type EnemyKind =
  | 'drone' | 'scout' | 'fighter' | 'gunship' | 'bomber'
  | 'tank' | 'carrier' | 'stealth' | 'interceptor' | 'asteroid'
  | 'comet' | 'miniboss' | 'boss';

export interface Enemy {
  kind: EnemyKind;
  x: number; y: number;
  vx: number; vy: number;
  hp: number; maxHp: number;
  eShield: number;
  radius: number;
  val: number;
  color: number;
  ft: number; fr: number;
  t: number;
  flashT: number;
  hitFlashT: number;
  sinAmp?: number; sinFreq?: number; sinPhase?: number;
  elite: boolean;
  phantom: boolean;
  phase: number;
  lastPhase: number;
  warnT: number;
  alive: boolean;
  view: Container;
  body: Container;
  flashOverlay?: Graphics;
  mirror?: Container;
  mirrorBody?: Container;
}

// Destructible boss appendage — 4 of these orbit the boss; core takes reduced damage while any are alive.
export interface BossPart {
  alive: boolean;
  hp: number;
  maxHp: number;
  angle: number;       // base angular offset
  dist: number;        // orbital radius from boss center
  r: number;           // collision radius
  orbitSpd: number;    // rad/s rotation around boss
  x: number;           // last-rendered world-space position (updated each draw)
  y: number;
}

export interface EnemyDef {
  hp: (d: number) => number;
  speed: () => { vx: number; vy: number };
  radius: number;
  val: number;
  color: number;
  draw: (g: Graphics, e: Enemy) => void;
  update: (e: Enemy, dt: number, W: number, H: number, target: { x: number; y: number }) => void;
  fireRate?: number;
}

const W0 = () => window.innerWidth;
const H0 = () => window.innerHeight;

export const ENEMY_DEFS: Record<Exclude<EnemyKind, 'asteroid' | 'miniboss' | 'boss' | 'comet'>, EnemyDef> = {
  drone: {
    hp: (d) => 6 * d,
    speed: () => ({ vx: -rnd(320, 500), vy: rnd(-60, 60) }),
    radius: 9, val: 8, color: 0xff44cc,
    draw: (g) => {
      g.clear();
      g.poly([10, 0, -6, -7, -3, 0, -6, 7]);
      g.fill({ color: 0xff44cc });
      g.stroke({ width: 2, color: 0xffaaff });
    },
    update: (e, dt) => {
      e.t += dt;
      e.x += e.vx * dt;
      e.y += e.vy * dt + Math.sin(e.t * (e.sinFreq ?? 4) + (e.sinPhase ?? 0)) * (e.sinAmp ?? 145) * dt;
    },
  },
  scout: {
    hp: (d) => 10 * d,
    speed: () => ({ vx: -rnd(240, 380), vy: 0 }),
    radius: 15, val: 10, color: 0xff4444,
    draw: (g) => {
      g.clear();
      g.poly([14, 0, -10, -10, -4, 0, -10, 10]);
      g.fill({ color: 0xff4444 });
      g.stroke({ width: 2, color: 0xffaaaa });
    },
    update: (e, dt) => {
      e.t += dt;
      e.x += e.vx * dt;
      e.y += Math.sin(e.t * (e.sinFreq ?? 2.5) + (e.sinPhase ?? 0)) * (e.sinAmp ?? 130) * dt;
    },
  },
  fighter: {
    hp: (d) => 30 * d,
    speed: () => ({ vx: -rnd(160, 240), vy: 0 }),
    radius: 22, val: 28, color: 0xff8844,
    draw: (g) => {
      g.clear();
      g.poly([22, 0, -16, -16, -8, 0, -16, 16]);
      g.fill({ color: 0xff8844 });
      g.stroke({ width: 2.5, color: 0xffccaa });
    },
    update: (e, dt, _W, _H, target) => {
      e.t += dt;
      e.x += e.vx * dt;
      const dy = Math.max(-260, Math.min(260, (target.y - e.y) * 1.2));
      e.vy += (dy - e.vy) * Math.min(1, 0.12 * 60 * dt);
      e.y += e.vy * dt;
    },
    fireRate: 2.2,
  },
  gunship: {
    hp: (d) => 75 * d,
    speed: () => ({ vx: -rnd(90, 130), vy: 0 }),
    radius: 38, val: 65, color: 0xaa44ff,
    draw: (g) => {
      g.clear();
      g.rect(-22, -16, 48, 32);
      g.fill({ color: 0xaa44ff });
      g.stroke({ width: 2.5, color: 0xddaaff });
    },
    update: (e, dt, _W, _H, target) => {
      e.t += dt;
      e.x += e.vx * dt;
      const dy = Math.max(-180, Math.min(180, (target.y - e.y) * 0.9));
      e.vy += (dy - e.vy) * Math.min(1, 0.08 * 60 * dt);
      e.y += e.vy * dt;
    },
    fireRate: 1.3,
  },
  bomber: {
    hp: (d) => 55 * d,
    speed: () => ({ vx: -rnd(65, 95), vy: 0 }),
    radius: 30, val: 70, color: 0xff5500,
    draw: (g) => {
      g.clear();
      g.circle(0, 0, 26);
      g.fill({ color: 0xff5500 });
      g.stroke({ width: 2.5, color: 0xffaa66 });
    },
    update: (e, dt, _W, _H, target) => {
      e.t += dt;
      e.x += e.vx * dt;
      const dy = Math.max(-120, Math.min(120, (target.y - e.y) * 0.8));
      e.vy += (dy - e.vy) * Math.min(1, 0.06 * 60 * dt);
      e.y += e.vy * dt;
    },
    fireRate: 3.5,
  },
  tank: {
    hp: (d) => 250 * d,
    speed: () => ({ vx: -rnd(38, 58), vy: 0 }),
    radius: 44, val: 130, color: 0xcc7700,
    draw: (g) => {
      g.clear();
      g.rect(-32, -22, 60, 44);
      g.fill({ color: 0xcc7700 });
      g.stroke({ width: 3, color: 0xffaa44 });
    },
    update: (e, dt, _W, _H, target) => {
      e.t += dt;
      e.x += e.vx * dt;
      const dy = Math.max(-80, Math.min(80, (target.y - e.y) * 0.5));
      e.vy += (dy - e.vy) * Math.min(1, 0.04 * 60 * dt);
      e.y += e.vy * dt;
    },
    fireRate: 2.4,
  },
  carrier: {
    hp: (d) => 160 * d,
    speed: () => ({ vx: -rnd(45, 70), vy: 0 }),
    radius: 38, val: 120, color: 0x00ccff,
    draw: (g) => {
      g.clear();
      g.ellipse(0, 0, 30, 18);
      g.fill({ color: 0x00ccff });
      g.stroke({ width: 2.5, color: 0xaaeeff });
    },
    update: (e, dt, _W, _H, target) => {
      e.t += dt;
      e.x += e.vx * dt;
      const dy = Math.max(-100, Math.min(100, (target.y - e.y) * 0.7));
      e.vy += (dy - e.vy) * Math.min(1, 0.05 * 60 * dt);
      e.y += e.vy * dt;
    },
  },
  stealth: {
    hp: (d) => 8 * d,
    speed: () => ({ vx: -rnd(280, 420), vy: rnd(-50, 50) }),
    radius: 12, val: 22, color: 0xaa44ff,
    draw: (g) => {
      g.clear();
      g.poly([14, 0, -8, -6, -8, 6]);
      g.fill({ color: 0xaa44ff });
      g.stroke({ width: 2, color: 0xddaaff });
    },
    update: (e, dt) => {
      e.t += dt;
      e.x += e.vx * dt;
      e.y += Math.sin(e.t * (e.sinFreq ?? 2.5) + (e.sinPhase ?? 0)) * (e.sinAmp ?? 110) * dt;
    },
  },
  interceptor: {
    hp: (d) => 20 * d,
    speed: () => ({ vx: -rnd(380, 480), vy: 0 }),
    radius: 14, val: 35, color: 0xffdd00,
    draw: (g) => {
      g.clear();
      g.poly([12, 0, -10, -8, -4, 0, -10, 8]);
      g.fill({ color: 0xffdd00 });
      g.stroke({ width: 2, color: 0xffffaa });
    },
    update: (e, dt, _W, _H, target) => {
      e.t += dt;
      const dx = target.x - e.x, dy = target.y - e.y;
      const d = Math.hypot(dx, dy) || 1;
      const orbitR = 220;
      if (d < orbitR) {
        const a = Math.atan2(dy, dx) + Math.PI / 2;
        e.vx = Math.cos(a) * 240;
        e.vy = Math.sin(a) * 240;
      }
      e.x += e.vx * dt;
      e.y += e.vy * dt;
    },
    fireRate: 1.3,
  },
};

interface ExplParticle { x: number; y: number; vx: number; vy: number; life: number; life0: number; r: number; color: number; }

export class EnemyManager {
  enemies: Enemy[] = [];
  explosions: ExplParticle[] = [];
  layer: Container;
  explLayer!: Graphics;

  constructor(stage: Container) {
    this.layer = new Container();
    stage.addChild(this.layer);
    this.explLayer = new Graphics();
    stage.addChild(this.explLayer);
  }

  spawn(kind: EnemyKind, d = 1, mod: 'HASTE' | 'SWARM' | 'SHIELDED' | 'PHANTOM' | null = null): Enemy {
    if (kind === 'asteroid') return this.spawnAsteroid();
    if (kind === 'comet') return this.spawnComet();
    if (kind === 'miniboss') return this.spawnMiniBoss(d);
    if (kind === 'boss') return this.spawnBoss(d);
    const def = ENEMY_DEFS[kind];
    const v = def.speed();
    const view = new Container();
    const tex = SPRITES.enemy[kind as keyof typeof SPRITES.enemy] ?? SPRITES.enemy.drone;
    const body = new Sprite(tex);
    body.anchor.set(0.5, 0.5);
    // Per-spawn variant: tiny hue jitter via tint (220..255 per channel = ~±13% RGB shift),
    // sub-pixel scale jitter, and a small random rotation for kinds where it reads naturally.
    const tr = 220 + Math.floor(Math.random() * 35);
    const tg = 220 + Math.floor(Math.random() * 35);
    const tb = 220 + Math.floor(Math.random() * 35);
    body.tint = (tr << 16) | (tg << 8) | tb;
    const sJit = 0.97 + Math.random() * 0.06;
    body.scale.set(sJit, sJit);
    if (kind === 'drone' || kind === 'scout' || kind === 'stealth') {
      body.rotation = (Math.random() - 0.5) * 0.18;
    }
    view.addChild(body);
    this.layer.addChild(view);
    // Per-kind color halo — separates 9 archetypes from world bloom soup.
    // Elite path below overrides with gold glow; warning state may also override.
    try {
      view.filters = [new GlowFilter({ distance: 14, outerStrength: 2.0, innerStrength: 0.3, color: def.color, quality: 0.4 }) as any];
    } catch {}
    const e: Enemy = {
      kind,
      x: W0() + 60,
      y: rnd(50, H0() - 50),
      vx: v.vx,
      vy: v.vy,
      hp: def.hp(d),
      maxHp: def.hp(d),
      radius: def.radius,
      val: def.val,
      color: def.color,
      ft: rndI(0, 60),
      fr: def.fireRate ? def.fireRate * 60 : 0,
      t: 0,
      flashT: 14,
      hitFlashT: 0,
      eShield: 0,
      elite: false,
      phantom: false,
      phase: 0, lastPhase: 0, warnT: 0,
      sinAmp: rnd(70, 140),
      sinFreq: rnd(2, 4),
      sinPhase: rnd(0, Math.PI * 2),
      alive: true,
      view,
      body,
    };
    view.x = e.x;
    view.y = e.y;

    // Wave modifiers
    if (mod === 'HASTE') { e.vx *= 1.4; e.vy *= 1.4; }
    if (mod === 'SHIELDED') { e.eShield = Math.floor(e.maxHp * 0.3); }
    if (mod === 'PHANTOM') { e.phantom = true; }

    // Elite roll: 15% chance per standard spawn
    if (Math.random() < 0.15) {
      e.elite = true;
      e.hp *= 2; e.maxHp *= 2;
      e.val *= 3;
      e.vx *= 1.4; e.vy *= 1.4;
      e.color = 0xffcc00;
      try {
        e.view.filters = [new GlowFilter({ distance: 14, outerStrength: 1.8, innerStrength: 0.4, color: 0xffcc00, quality: 0.4 }) as any];
      } catch {}
    }

    this.enemies.push(e);
    return e;
  }

  spawnAsteroidAt(x: number, y: number, sz: number, vx?: number, vy?: number): Enemy {
    const e = this.makeAsteroid(sz, x, y, vx, vy);
    return e;
  }

  private makeAsteroid(sz: number, x?: number, y?: number, vx?: number, vy?: number): Enemy {
    const r = 12 + sz * 8;
    const view = new Container();
    const body: Container = new Graphics();
    const g = body as Graphics;
    // v0.6 rocky polygon: 7..12 random verts, per-vertex radius jitter rnd(r*0.68, r*1.28)
    const verts = 7 + Math.floor(Math.random() * 6);
    const path: number[] = [];
    for (let i = 0; i < verts; i++) {
      const a = (i / verts) * Math.PI * 2;
      const rr = r * (0.68 + Math.random() * 0.6);
      path.push(Math.cos(a) * rr, Math.sin(a) * rr);
    }
    g.poly(path);
    g.fill({ color: 0x2a2a2a });
    g.stroke({ width: 1.5, color: 0x999999, alpha: 0.85 });
    // 2 craters at v0.6 offsets
    g.circle(r * 0.28, r * 0.10, r * 0.20);
    g.fill({ color: 0x1a1a1a });
    g.circle(-r * 0.22, -r * 0.28, r * 0.14);
    g.fill({ color: 0x1a1a1a });
    view.addChild(body);
    this.layer.addChild(view);
    const e: Enemy = {
      kind: 'asteroid',
      x: x ?? W0() + r + 10,
      y: y ?? rnd(r + 20, H0() - r - 20),
      // Bigger asteroids enter slower — gives the player time to register them
      vx: vx ?? -rnd(sz >= 6 ? 50 : 80, sz >= 6 ? 110 : 160),
      vy: vy ?? rnd(-35, 35),
      hp: sz * 28, maxHp: sz * 28,
      radius: r, val: sz * 5, color: 0x888888,
      ft: 0, fr: 0, t: 0, flashT: 14, hitFlashT: 0,
      eShield: 0, elite: false, phantom: false,
      phase: 0, lastPhase: 0, warnT: 0,
      alive: true, view, body,
    };
    (e as any).sz = sz;
    view.x = e.x; view.y = e.y;
    this.enemies.push(e);
    return e;
  }

  private spawnAsteroid(): Enemy {
    // Weighted size distribution — sz 1..8, with rare giants (sz 6/7/8) for spectacle
    // 42/22/14/8/6/4/2.5/1.5 %
    const r = Math.random();
    const sz = r < 0.42 ? 1
             : r < 0.64 ? 2
             : r < 0.78 ? 3
             : r < 0.86 ? 4
             : r < 0.92 ? 5
             : r < 0.96 ? 6
             : r < 0.985 ? 7
             : 8;
    return this.makeAsteroid(sz);
  }

  private spawnComet(): Enemy {
    const W = W0(), H = H0();
    // Spawn from a random screen edge, head toward a random target across the screen
    const edge = rndI(0, 4);
    let sx: number, sy: number, tx: number, ty: number;
    if (edge === 0) { sx = rnd(0, W); sy = -40; tx = rnd(0, W); ty = H + 40; }
    else if (edge === 1) { sx = W + 40; sy = rnd(0, H); tx = -40; ty = rnd(0, H); }
    else if (edge === 2) { sx = rnd(0, W); sy = H + 40; tx = rnd(0, W); ty = -40; }
    else { sx = -40; sy = rnd(0, H); tx = W + 40; ty = rnd(0, H); }
    const dx = tx - sx, dy = ty - sy;
    const d = Math.hypot(dx, dy) || 1;
    const sp = rnd(480, 720);

    const view = new Container();
    const body = new Graphics();
    // Bright white-cyan core — reads as a glowing comet head
    body.circle(0, 0, 9); body.fill({ color: 0xaaffff, alpha: 0.45 });
    body.circle(0, 0, 6); body.fill({ color: 0xffffff });
    body.circle(0, 0, 3); body.fill({ color: 0xeeffff });
    view.addChild(body);
    // Trail Graphics — added to layer (not view) so polyline points are world-space
    const trailG = new Graphics();
    this.layer.addChild(trailG);
    this.layer.addChild(view);

    const e: Enemy = {
      kind: 'comet',
      x: sx, y: sy,
      vx: (dx / d) * sp, vy: (dy / d) * sp,
      hp: 18, maxHp: 18,
      radius: 8, val: 35, color: 0xaaffff,
      ft: 0, fr: 0, t: 0, flashT: 14, hitFlashT: 0,
      eShield: 0, elite: false, phantom: false,
      phase: 0, lastPhase: 0, warnT: 0,
      alive: true, view, body,
    };
    (e as any).trail = [] as Array<{ x: number; y: number }>;
    (e as any).trailG = trailG;
    view.x = sx; view.y = sy;
    this.enemies.push(e);
    return e;
  }

  private spawnMiniBoss(d: number): Enemy {
    const view = new Container();
    // Spokes container (rotates) — drawn behind body
    const spokes = new Container();
    const spokesGfx = new Graphics();
    spokes.addChild(spokesGfx);
    spokesGfx.stroke({ width: 4, color: 0xff6600 });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      spokesGfx.moveTo(Math.cos(a) * 20, Math.sin(a) * 20);
      spokesGfx.lineTo(Math.cos(a) * 52, Math.sin(a) * 52);
    }
    spokesGfx.stroke({ width: 4, color: 0xff6600 });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      spokesGfx.circle(Math.cos(a) * 52, Math.sin(a) * 52, 8);
      spokesGfx.fill({ color: 0xff4400 });
    }
    view.addChild(spokes);
    // Core (wavy 8-vert blob, redrawn each frame)
    const body = new Graphics();
    view.addChild(body);
    let miniGlow: GlowFilter | null = null;
    try {
      miniGlow = new GlowFilter({ distance: 18, outerStrength: 2.2, innerStrength: 0.5, color: 0xff6622, quality: 0.4 });
      view.filters = [miniGlow as any];
    } catch {}
    this.layer.addChild(view);
    const e: Enemy = {
      kind: 'miniboss',
      x: W0() + 90, y: H0() / 2,
      vx: -60, vy: 0,
      hp: 420 * d, maxHp: 420 * d,
      radius: 50, val: 350, color: 0xff4400,
      ft: 0, fr: 55, t: 0, flashT: 20, hitFlashT: 0,
      eShield: 0, elite: false, phantom: false,
      phase: 0, lastPhase: 0, warnT: 0,
      alive: true, view, body,
    };
    (e as any).spokes = spokes;
    (e as any).glow = miniGlow;
    view.x = e.x; view.y = e.y;
    this.enemies.push(e);
    return e;
  }

  private spawnBoss(d: number): Enemy {
    const view = new Container();
    // Inner spokes (counter-rotate)
    const innerSpokes = new Container();
    const innerGfx = new Graphics();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      innerGfx.moveTo(0, 0);
      innerGfx.lineTo(Math.cos(a) * 88, Math.sin(a) * 88);
    }
    innerGfx.stroke({ width: 5, color: 0xff00b4, alpha: 0.5 });
    innerSpokes.addChild(innerGfx);
    view.addChild(innerSpokes);
    // Outer orbital ring (rotates)
    const ring = new Container();
    const ringGfx = new Graphics();
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      ringGfx.circle(Math.cos(a) * 105, Math.sin(a) * 105, 13);
      ringGfx.fill({ color: 0x110011 });
      ringGfx.stroke({ width: 2.5, color: i % 2 === 0 ? 0xff00ff : 0xff8800 });
    }
    ring.addChild(ringGfx);
    view.addChild(ring);
    // Core (wavy 20-vert blob, redrawn each frame)
    const body = new Graphics();
    view.addChild(body);
    let bossGlow: GlowFilter | null = null;
    try {
      bossGlow = new GlowFilter({ distance: 24, outerStrength: 2.6, innerStrength: 0.5, color: 0xff44ff, quality: 0.4 });
      view.filters = [bossGlow as any];
    } catch {}
    this.layer.addChild(view);
    // F7 MIRROR — twin visual that main.ts toggles on bossArena==='MIRROR'
    const mirror = new Container();
    const mirrorRing = new Container();
    const mirrorRingGfx = new Graphics();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      mirrorRingGfx.circle(Math.cos(a) * 68, Math.sin(a) * 68, 11);
      mirrorRingGfx.fill({ color: 0x110011 });
      mirrorRingGfx.stroke({ width: 2.5, color: i % 2 === 0 ? 0xff00ff : 0xff8800 });
    }
    mirrorRing.addChild(mirrorRingGfx);
    mirror.addChild(mirrorRing);
    const mirrorBody = new Graphics();
    mirror.addChild(mirrorBody);
    mirror.scale.x = -1;
    mirror.visible = false;
    let mirrorGlow: GlowFilter | null = null;
    try {
      mirrorGlow = new GlowFilter({ distance: 24, outerStrength: 2.6, innerStrength: 0.5, color: 0xff44ff, quality: 0.4 });
      mirror.filters = [mirrorGlow as any];
    } catch {}
    this.layer.addChild(mirror);
    const e: Enemy = {
      kind: 'boss',
      x: W0() + 180, y: H0() / 2,
      vx: -50, vy: 0,
      hp: 1800 * d, maxHp: 1800 * d,
      radius: 110, val: 1200, color: 0xff00ff,
      ft: 0, fr: 38, t: 0, flashT: 30, hitFlashT: 0,
      eShield: 0, elite: false, phantom: false,
      phase: 0, lastPhase: 0, warnT: 0,
      alive: true, view, body, mirror, mirrorBody,
    };
    (e as any).ring = ring;
    (e as any).innerSpokes = innerSpokes;
    (e as any).mirrorRing = mirrorRing;
    (e as any).glow = bossGlow;
    (e as any).mirrorGlow = mirrorGlow;
    // 4 destructible boss parts — orbital pods at NW/NE/SW/SE around the core.
    // Core is mostly invulnerable until all 4 are destroyed (see main.ts collision path).
    const partHp = Math.round(130 * d);
    const parts: BossPart[] = [];
    const partAngles = [-Math.PI * 0.25, Math.PI * 0.25, Math.PI * 0.75, -Math.PI * 0.75];
    for (const a of partAngles) {
      parts.push({
        alive: true,
        hp: partHp, maxHp: partHp,
        angle: a,
        dist: 130,
        r: 22,
        orbitSpd: 0.18,
        x: 0, y: 0,
      });
    }
    (e as any).parts = parts;
    view.x = e.x; view.y = e.y;
    this.enemies.push(e);
    return e;
  }

  // Redraw boss/miniboss wavy core each frame (called from update loop).
  private drawBossCore(e: Enemy): void {
    const t = e.t;
    const phaseColors = [0xff00ff, 0xff4400, 0xffff00];
    const phaseCol = phaseColors[Math.min(2, e.phase)];
    const g = e.body as Graphics;
    g.clear();
    const parts = (e as any).parts as BossPart[] | undefined;
    const partsAlive = parts ? parts.filter((p) => p.alive).length : 0;
    const dimA = partsAlive > 0 ? 0.42 : 1.0;
    // Dreadnought hull — angular mechanical
    g.poly([-86, 0, -68, -22, -22, -40, 30, -44, 68, -26, 80, 0, 68, 26, 30, 44, -22, 40, -68, 22]);
    g.fill({ color: 0x150028 });
    g.stroke({ width: 3.5, color: phaseCol, alpha: dimA });
    // Top wing spar
    g.poly([-15, -40, 28, -44, 44, -78, 20, -84, -8, -68]);
    g.fill({ color: 0x0d001a });
    g.stroke({ width: 2, color: phaseCol, alpha: dimA * 0.8 });
    // Bottom wing spar
    g.poly([-15, 40, 28, 44, 44, 78, 20, 84, -8, 68]);
    g.fill({ color: 0x0d001a });
    g.stroke({ width: 2, color: phaseCol, alpha: dimA * 0.8 });
    // Command bridge
    g.poly([-68, -14, -38, -20, -8, -18, -8, 18, -38, 20, -68, 14]);
    g.fill({ color: 0x2a0042 });
    g.stroke({ width: 2, color: phaseCol, alpha: dimA });
    // Forward cannon array
    g.rect(-92, -5, 16, 10);
    g.fill({ color: 0x1a002e });
    g.stroke({ width: 2, color: phaseCol, alpha: dimA });
    g.rect(-94, -3.5, 22, 2.5);
    g.fill({ color: phaseCol });
    g.rect(-94, 1, 22, 2.5);
    g.fill({ color: phaseCol });
    // Wing weapon hardpoints
    for (const wy of [-64, 64]) {
      g.rect(8, wy - 6, 18, 12);
      g.fill({ color: 0x220033 });
      g.stroke({ width: 1.5, color: phaseCol, alpha: dimA * 0.8 });
      g.rect(2, wy - 2, 24, 4);
      g.fill({ color: phaseCol, alpha: 0.7 });
    }
    // Engine bank at rear
    for (const ey of [-18, 0, 18]) {
      g.rect(72, ey - 6, 14, 12);
      g.fill({ color: 0x050010 });
      g.stroke({ width: 1.5, color: 0x4488ff });
      g.circle(79, ey, 4.5);
      g.fill({ color: 0x88ccff, alpha: 0.85 });
    }
    // Pulsing core
    const pr = 28 + Math.sin(t * 4.2) * 7;
    g.circle(0, 0, pr);
    g.fill({ color: phaseCol, alpha: partsAlive > 0 ? 0.40 : 0.90 });
    g.circle(0, 0, pr * 0.40);
    g.fill({ color: 0xffffff, alpha: partsAlive > 0 ? 0.50 : 0.90 });
    // Render each alive boss part — orbital pod with inner core dot
    if (parts) {
      for (const p of parts) {
        if (!p.alive) continue;
        const px = Math.cos(p.angle + t * p.orbitSpd) * p.dist;
        const py = Math.sin(p.angle + t * p.orbitSpd) * p.dist;
        // Cache live world-space pos for collision tests
        p.x = e.x + px;
        p.y = e.y + py;
        const hpFrac = Math.max(0, p.hp / p.maxHp);
        // Damaged parts get more cyan core wobble
        const wob = 1 + (1 - hpFrac) * 0.3 * Math.sin(t * 18);
        g.circle(px, py, p.r * wob);
        g.fill({ color: 0x230033 });
        g.stroke({ width: 2.4, color: 0x00ffcc, alpha: 0.95 });
        // Inner pod core, color shifts amber→red as damaged
        const podCol = hpFrac > 0.5 ? 0x00ffcc : hpFrac > 0.25 ? 0xffaa00 : 0xff4444;
        g.circle(px, py, p.r * 0.45);
        g.fill({ color: podCol, alpha: 0.9 });
        g.circle(px, py, p.r * 0.20);
        g.fill({ color: 0xffffff, alpha: 0.9 });
        // HP arc around the pod
        const startA = -Math.PI / 2;
        if (hpFrac > 0) {
          g.moveTo(px + Math.cos(startA) * (p.r + 3), py + Math.sin(startA) * (p.r + 3));
          g.arc(px, py, p.r + 3, startA, startA + Math.PI * 2 * hpFrac);
          g.stroke({ width: 2, color: podCol, alpha: 0.95 });
        }
      }
    }
    // Mirror body — phantom dreadnought (MIRROR arena)
    if (e.mirrorBody) {
      const m = e.mirrorBody as Graphics;
      m.clear();
      m.poly([-60, 0, -47, -15, -15, -28, 21, -30, 47, -18, 56, 0, 47, 18, 21, 30, -15, 28, -47, 15]);
      m.fill({ color: 0x1a0033 });
      m.stroke({ width: 3, color: phaseCol });
      m.poly([-10, -28, 19, -30, 30, -54, 14, -58, -5, -47]);
      m.fill({ color: 0x0d001a });
      m.stroke({ width: 1.5, color: phaseCol, alpha: 0.7 });
      m.poly([-10, 28, 19, 30, 30, 54, 14, 58, -5, 47]);
      m.fill({ color: 0x0d001a });
      m.stroke({ width: 1.5, color: phaseCol, alpha: 0.7 });
      m.circle(0, 0, pr);
      m.fill({ color: phaseCol, alpha: 0.85 });
    }
    // Rotate ring + inner spokes
    const ring = (e as any).ring as Container | undefined;
    const inner = (e as any).innerSpokes as Container | undefined;
    const mRing = (e as any).mirrorRing as Container | undefined;
    if (ring) ring.rotation = t * 0.72;
    if (inner) inner.rotation = -t * 1.15;
    if (mRing) mRing.rotation = -t * 0.72; // mirror flips rotation direction
  }

  private drawMiniBossCore(e: Enemy): void {
    const t = e.t;
    const g = e.body as Graphics;
    g.clear();
    // Assault Cruiser hull — angular mechanical (spokes rotate behind)
    g.poly([-44, 0, -30, -14, -6, -20, 18, -20, 36, -10, 40, 0, 36, 10, 18, 20, -6, 20, -30, 14]);
    g.fill({ color: 0x1a0500 });
    g.stroke({ width: 2.5, color: 0xff4400 });
    // Forward swept wings
    g.poly([-6, -20, 16, -20, 30, -40, 14, -44, -2, -30]);
    g.fill({ color: 0x120300 });
    g.stroke({ width: 1.5, color: 0xff6600, alpha: 0.8 });
    g.poly([-6, 20, 16, 20, 30, 40, 14, 44, -2, 30]);
    g.fill({ color: 0x120300 });
    g.stroke({ width: 1.5, color: 0xff6600, alpha: 0.8 });
    // Cockpit nose
    g.poly([-42, -8, -52, 0, -42, 8, -28, 6, -28, -6]);
    g.fill({ color: 0x3a0c00 });
    g.stroke({ width: 2, color: 0xff8800 });
    // Forward gun barrels
    g.rect(-58, -3, 12, 6);
    g.fill({ color: 0x0a0000 });
    g.rect(-58, -2, 16, 2);
    g.fill({ color: 0xff6600 });
    g.rect(-58, 0.2, 16, 2);
    g.fill({ color: 0xff6600 });
    // Engine section
    g.rect(36, -14, 10, 28);
    g.fill({ color: 0x0f0200 });
    g.stroke({ width: 1.5, color: 0xff4400 });
    for (const ey of [-7, 7]) {
      g.circle(41, ey, 4);
      g.fill({ color: 0xff9944, alpha: 0.85 });
    }
    // Pulsing core
    const pr2 = 14 + Math.sin(t * 5) * 4;
    g.circle(0, 0, pr2);
    g.fill({ color: 0xff8800, alpha: 0.85 });
    g.circle(0, 0, pr2 * 0.38);
    g.fill({ color: 0xffffff, alpha: 0.95 });
    // Rotate spokes
    const spokes = (e as any).spokes as Container | undefined;
    if (spokes) spokes.rotation = t * 0.9;
  }

  update(dt: number, W: number, H: number, target: { x: number; y: number }): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      if (!e.alive) continue;
      if (e.kind === 'asteroid') {
        e.t += dt;
        e.x += e.vx * dt;
        e.y += e.vy * dt;
        if (e.y < e.radius) { e.y = e.radius; e.vy *= -1; }
        if (e.y > H - e.radius) { e.y = H - e.radius; e.vy *= -1; }
        e.body.rotation += dt * 0.5;
      } else if (e.kind === 'comet') {
        e.t += dt;
        e.x += e.vx * dt;
        e.y += e.vy * dt;
        // Despawn (no score) when exiting the screen with margin — destroy view + trail
        if (e.x < -100 || e.x > W + 100 || e.y < -100 || e.y > H + 100) {
          e.alive = false;
          e.view.parent?.removeChild(e.view);
          e.view.destroy({ children: true });
          const tg0 = (e as any).trailG as Graphics | undefined;
          if (tg0) { tg0.parent?.removeChild(tg0); tg0.destroy(); }
          continue;
        }
        // Trail — push current pos, drop tail when too long, redraw as fading polyline
        const trail = (e as any).trail as Array<{ x: number; y: number }>;
        trail.push({ x: e.x, y: e.y });
        if (trail.length > 36) trail.shift();
        const tg = (e as any).trailG as Graphics;
        tg.clear();
        for (let k = 1; k < trail.length; k++) {
          const a = k / trail.length;
          const w = 0.5 + a * 5;
          const col = a < 0.4 ? 0x4488ff : a < 0.8 ? 0x88ccff : 0xffffff;
          tg.moveTo(trail[k - 1].x, trail[k - 1].y);
          tg.lineTo(trail[k].x, trail[k].y);
          tg.stroke({ width: w, color: col, alpha: a * 0.85 });
        }
      } else if (e.kind === 'miniboss' || e.kind === 'boss') {
        e.t += dt;
        const phaseT = 1 - e.hp / e.maxHp;
        const newPh = phaseT < 0.34 ? 0 : phaseT < 0.67 ? 1 : 2;
        if (newPh !== e.phase) {
          e.lastPhase = e.phase;
          e.phase = newPh;
          e.warnT = 0.5;
          // Phase-tinted halo: P1 magenta/orange → P2 orange → P3 red, intensifying
          const phaseCol = e.kind === 'boss'
            ? (newPh === 0 ? 0xff44ff : newPh === 1 ? 0xff8800 : 0xff0000)
            : (newPh === 0 ? 0xff6622 : newPh === 1 ? 0xff8800 : 0xff0000);
          const phaseOuter = newPh === 0 ? 2.4 : newPh === 1 ? 2.8 : 3.2;
          const g = (e as any).glow as GlowFilter | null;
          if (g) { g.color = phaseCol; g.outerStrength = phaseOuter; }
          const mg = (e as any).mirrorGlow as GlowFilter | null;
          if (mg) { mg.color = phaseCol; mg.outerStrength = phaseOuter; }
        }
        if (e.warnT > 0) e.warnT -= dt;

        // Phase-3 dash: teleport every 4s
        const dashCadence = (e as any).dashT || 0;
        if (e.phase === 2 && e.kind === 'boss') {
          (e as any).dashT = (dashCadence + dt);
          if ((e as any).dashT >= 4) {
            (e as any).dashT = 0;
            e.x = Math.max(60, Math.min(W - 60, target.x + (Math.random() - 0.5) * 200));
            e.y = Math.max(60, Math.min(H - 60, target.y + (Math.random() - 0.5) * 200));
            e.vx = 0; e.vy = 0;
          }
        }

        const sp = e.kind === 'boss'
          ? (e.phase === 0 ? 155 : e.phase === 1 ? 240 : 350)
          : (e.phase === 0 ? 120 : e.phase === 1 ? 185 : 260);
        const dx = target.x - e.x, dy = target.y - e.y;
        const d = Math.hypot(dx, dy) || 1;
        // Keep a minimum orbit distance — retreat when too close so player can escape
        const ORBIT_R = e.kind === 'boss' ? 200 : 140;
        const chase = d > ORBIT_R ? sp : -sp * 0.55;
        e.vx += ((dx / d) * chase - e.vx) * Math.min(1, 0.04 * 60 * dt);
        e.vy += ((dy / d) * chase - e.vy) * Math.min(1, 0.04 * 60 * dt);
        e.x += e.vx * dt;
        e.y += e.vy * dt;
        // Phase-based fire rate scaling
        if (e.kind === 'boss') e.fr = e.phase === 0 ? 22 : e.phase === 1 ? 15 : 8;
        if (e.kind === 'miniboss') e.fr = e.phase === 0 ? 36 : e.phase === 1 ? 24 : 14;
        // Animated core redraw — wavy radius + rotating spokes/orbital ring
        if (e.kind === 'boss') this.drawBossCore(e);
        else this.drawMiniBossCore(e);
      } else {
        ENEMY_DEFS[e.kind as Exclude<EnemyKind, 'asteroid' | 'miniboss' | 'boss' | 'comet'>].update(e, dt, W, H, target);
        // Carrier — spawn drones every 5s while on screen
        if (e.kind === 'carrier' && e.x < W) {
          const cd = (e as any).carrierT || 0;
          (e as any).carrierT = cd + dt;
          if ((e as any).carrierT >= 5) {
            (e as any).carrierT = 0;
            const drone = this.spawn('drone', 1);
            drone.x = e.x; drone.y = e.y;
            drone.vx = -rnd(180, 260);
            drone.vy = rnd(-60, 60);
          }
        }
      }

      if (e.flashT > 0) {
        e.flashT -= 1;
        e.body.alpha = 0.4 + 0.6 * (1 - e.flashT / 14);
      } else if (e.phantom) {
        e.body.alpha = 0.35 + 0.45 * Math.abs(Math.sin(e.t * 3.6));
      } else if (e.kind === 'stealth') {
        e.body.alpha = 0.18 + 0.65 * Math.abs(Math.sin(e.t * 1.1));
      } else {
        e.body.alpha = 1;
      }
      // Hit flash white overlay — expanding ripple + center pulse
      if (e.hitFlashT > 0) {
        e.hitFlashT -= 1;
        if (!e.flashOverlay) {
          e.flashOverlay = new Graphics();
          e.flashOverlay.circle(0, 0, e.radius);
          e.flashOverlay.fill({ color: 0xffffff });
          e.view.addChild(e.flashOverlay);
        }
        const t = 1 - e.hitFlashT / 6; // 0..1 since hit
        e.flashOverlay.alpha = (e.hitFlashT / 6) * 0.85;
        e.flashOverlay.scale.set(1 + t * 0.6, 1 + t * 0.6); // expanding ripple
        e.flashOverlay.visible = true;
      } else if (e.flashOverlay) {
        e.flashOverlay.visible = false;
        if (e.flashOverlay.scale.x !== 1) e.flashOverlay.scale.set(1, 1);
      }
      if (e.elite) {
        e.body.tint = 0xffcc00;
      }
      e.view.x = e.x;
      e.view.y = e.y;

      if (e.x < -120 || e.y < -120 || e.y > H + 120) {
        this.kill(e, false);
      }
    }

    // Asteroid ↔ asteroid mass-weighted elastic bounce
    for (let i = 0; i < this.enemies.length; i++) {
      const a = this.enemies[i];
      if (!a.alive || a.kind !== 'asteroid') continue;
      for (let j = i + 1; j < this.enemies.length; j++) {
        const b = this.enemies[j];
        if (!b.alive || b.kind !== 'asteroid') continue;
        const dx = b.x - a.x, dy = b.y - a.y;
        const d2 = dx * dx + dy * dy;
        const minD = a.radius + b.radius;
        if (d2 < minD * minD) {
          const ma = (a as any).sz || 2, mb = (b as any).sz || 2;
          const d = Math.sqrt(d2) || 1;
          const nx = dx / d, ny = dy / d;
          const dot = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
          if (dot < 0) {
            const f = (2 * dot) / (ma + mb);
            a.vx += f * mb * nx;
            a.vy += f * mb * ny;
            b.vx -= f * ma * nx;
            b.vy -= f * ma * ny;
          }
          // Separate
          const overlap = minD - d;
          a.x -= nx * overlap * 0.5;
          a.y -= ny * overlap * 0.5;
          b.x += nx * overlap * 0.5;
          b.y += ny * overlap * 0.5;
        }
      }
    }

    // Explosion particles — clear+redraw each frame
    this.explLayer.clear();
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      const p = this.explosions[i];
      p.life -= dt;
      if (p.life <= 0) { this.explosions.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= Math.pow(0.86, dt * 60);
      p.vy *= Math.pow(0.86, dt * 60);
      const frac = p.life / p.life0;
      this.explLayer.circle(p.x, p.y, p.r * (0.4 + frac * 0.6));
      this.explLayer.fill({ color: p.color, alpha: frac * 0.88 });
      if (frac > 0.55) {
        this.explLayer.circle(p.x, p.y, p.r * 0.35 * frac);
        this.explLayer.fill({ color: 0xffffff, alpha: frac * 0.65 });
      }
    }
    this.compact();
  }

  kill(e: Enemy, _scored: boolean): void {
    const cnt = Math.min(16, 4 + Math.floor(e.radius / 5));
    for (let i = 0; i < cnt; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 90 + Math.random() * 300;
      const lt = 0.25 + Math.random() * 0.35;
      this.explosions.push({
        x: e.x, y: e.y,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        life: lt, life0: lt,
        r: 1.8 + Math.random() * (e.radius * 0.30),
        color: i % 3 === 0 ? 0xffffff : i % 3 === 1 ? 0xffcc44 : e.color,
      });
    }
    e.alive = false;
    e.view.visible = false;
    e.view.parent?.removeChild(e.view);
    e.view.destroy({ children: true });
    // Comet trail Graphics lives on the layer (not on view) — clean it up too
    if (e.kind === 'comet') {
      const tg = (e as any).trailG as Graphics | undefined;
      if (tg) { tg.parent?.removeChild(tg); tg.destroy(); }
    }
    if (e.mirror) {
      e.mirror.visible = false;
      e.mirror.parent?.removeChild(e.mirror);
      e.mirror.destroy({ children: true });
      e.mirror = undefined;
      e.mirrorBody = undefined;
    }
  }

  private compact(): void {
    let w = 0;
    for (let r = 0; r < this.enemies.length; r++) {
      if (this.enemies[r].alive) {
        if (w !== r) this.enemies[w] = this.enemies[r];
        w += 1;
      }
    }
    this.enemies.length = w;
  }
}
